import { SparklesIcon } from '@heroicons/react/outline';
import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import { type RelatedTemplate, useTemplateTranslation } from './kit';

/** Other templates in the same family, then a way into the full catalogue. */
export default function TemplateRelated({
  templates,
}: {
  templates: RelatedTemplate[];
}) {
  const { t } = useTemplateTranslation();
  if (!templates.length) return null;

  return (
    <section className='mx-auto max-w-6xl px-4 pt-24 sm:px-6 sm:pt-32 lg:px-8'>
      <div className='flex flex-wrap items-end justify-between gap-x-10 gap-y-4'>
        <SectionHeading
          size='md'
          title={t('More templates like this.')}
          tail={t('Free to try, no sign-up needed for most.')}
        />
        <Link
          href='/templates'
          className='inline-flex items-center gap-1.5 text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
        >
          {t('Browse all templates')}
          <ArrowGlyph />
        </Link>
      </div>

      <ul className='mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
        {templates.map((template) => (
          <li key={template.slug}>
            <Link
              href={template.href}
              className={clsxm(
                'group flex h-full items-start gap-4 rounded-[20px] p-5 transition duration-200 hover:ring-[#0f0f28]/[0.16] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:ring-white/20',
                tone.surface,
                tone.line,
              )}
            >
              <span
                className={clsxm(
                  'flex h-10 w-10 flex-none items-center justify-center rounded-xl dark:bg-white/[0.06] dark:text-primary-300',
                  template.iconBackground,
                  template.iconForeground,
                )}
              >
                <SparklesIcon className='h-5 w-5' aria-hidden='true' />
              </span>
              <span className='min-w-0 flex-1'>
                <span
                  className={clsxm(
                    'flex items-start justify-between gap-2 text-[15px] font-semibold leading-6',
                    tone.ink,
                  )}
                >
                  {t(template.title)}
                  <ArrowGlyph className='mt-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100' />
                </span>
                <span
                  className={clsxm(
                    'mt-1 line-clamp-2 text-[14px] leading-6',
                    tone.body,
                  )}
                >
                  {t(template.description)}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
